import { ReactNode } from "react";
import classNames from "classnames";
import { EmailField } from "../../blocks/fields/EmailField";
import { Button } from "../../elements/Button";
import { useSuppressionCheck } from "./useSuppressionCheck";

export interface SuppressionCheckFormProps {
  name: string;
  check: ReturnType<typeof useSuppressionCheck>;
  checkText: string;
  removeText: string;
  /** Shown in the "listed" alert, under its message. */
  details?: ReactNode;
}

// mb-2 matches the field's message slot, lining the button up with the input.
const CheckButtonClass = "flex-shrink-0 mb-2";
// Not the page Button: its .btn padding made the alert taller than a line of text.
const RemoveLinkClass =
  "link-primary flex-shrink-0 p-0 border-0 bg-transparent text-decoration-underline";

/** An address and a check button on one line, and what the check found below. */
export function SuppressionCheckForm({
  name,
  check,
  checkText,
  removeText,
  details,
}: SuppressionCheckFormProps) {
  const {
    emailAddress,
    setEmailAddress,
    listed,
    checkResult,
    removeResult,
    isSubmitting,
    runCheck,
    runRemove,
  } = check;
  const result = removeResult ?? checkResult;
  const resultClass = !result
    ? "invisible"
    : result.success
    ? "alert-success"
    : listed
    ? "alert-info"
    : "alert-danger";

  // Its own form, so autofill doesn't fill the other panel too. The alert is always
  // laid out, hidden until there's a result, so a result doesn't shift the page.
  return (
    <form
      name={name}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        runCheck();
      }}
    >
      <div className="d-flex align-items-end gap-2">
        <EmailField
          label="Email Address"
          name={name}
          value={emailAddress}
          onChange={setEmailAddress}
          disabled={isSubmitting}
          className="flex-grow-1"
        />
        <Button
          text={checkText}
          variant="primary"
          type="submit"
          className={CheckButtonClass}
          disabled={isSubmitting}
          showSpinner={isSubmitting}
        />
      </div>
      <div
        className={classNames("alert mb-0", resultClass)}
        role="status"
        aria-hidden={result ? undefined : true}
      >
        <div className="d-flex align-items-center gap-3">
          <span className="flex-grow-1">{result?.message ?? " "}</span>
          {listed && (
            <button
              type="button"
              className={RemoveLinkClass}
              disabled={isSubmitting}
              onClick={runRemove}
            >
              {removeText}
            </button>
          )}
        </div>
        {listed && details}
      </div>
    </form>
  );
}
